// The library: the owner's own dashboards and configs, what's shared with them, and TUNE's catalogue, a view each
// under one scope bar. Whatever's here reaches the owner's TUNE MFDs by itself; a file from this computer is added
// in a sheet (`?sheet=add`).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import type { ReactNode } from 'react';
import { data, problem } from '../../api';
import { useAuth } from '../../auth';
import { fetchFile, kindWord, size, sourceWords } from '../../items';
import { count, dayText } from '../../lib/words';
import { keys, useVehicles } from '../../queries';
import { Page } from '../../shell/Page';
import {
  Banner,
  Button,
  CardLink,
  EmptyState,
  ErrorBanner,
  ListControl,
  ListSection,
  SavingSwitch,
  ScopeBar,
  Sheet,
  Skeleton,
  useConfirm,
  useRouteSheet,
  useToast,
} from '../../ui';
import { BooksIcon, PlusIcon, ShareNetworkIcon, SquaresFourIcon } from '../../ui/icons';
import { AddFileSheet } from './AddFileSheet';
import { ItemThumb } from './parts';
import s from './Library.module.css';

type SharedItem = components['schemas']['SharedItem'];
type Entry = components['schemas']['CatalogueEntry'];
type Vehicle = components['schemas']['Vehicle'];

/** A view of the library: its scope bar, Add a file, and the view. */
function LibraryPage({ tab, children }: { tab: string; children: ReactNode }) {
  const [adding, setAdding] = useRouteSheet('add');
  return (
    <Page
      title="Library"
      tab={tab}
      root
      subtitle="Dashboards and configs for your TUNE MFDs. Whatever's here reaches them by itself."
      headActions={
        <Button size="small" icon={<PlusIcon size={16} weight="bold" />} onClick={() => setAdding(true)}>
          Add a file
        </Button>
      }
    >
      <ScopeBar
        label="The library"
        scopes={[
          { to: '/library', label: 'Yours', end: true },
          { to: '/library/shared', label: 'Shared with you' },
          { to: '/library/catalogue', label: "TUNE's catalogue" },
        ]}
      />
      {children}
      <AddFileSheet open={adding} onClose={() => setAdding(false)} />
    </Page>
  );
}

/** The owner's own: from their MFDs, TUNE's theme editor, or added here. */
export function Library() {
  const api = useAuth().api('owners');
  const [, setAdding] = useRouteSheet('add');
  const library = useQuery({ queryKey: keys.library, queryFn: () => data(api.GET('/library')) });
  const items = library.data?.items ?? [];
  return (
    <LibraryPage tab="Library">
      {library.isPending && <Skeleton blocks={3} lines={0} label="Loading your library" />}
      {library.isError && <ErrorBanner retry={() => void library.refetch()}>Your library couldn't load.</ErrorBanner>}
      {library.isSuccess && items.length === 0 && (
        <EmptyState
          icon={<BooksIcon size={56} />}
          title="Nothing yet"
          action={<Button onClick={() => setAdding(true)}>Add a file</Button>}
        >
          A dashboard or config made or changed on an MFD comes here by itself, as do those from TUNE's theme editor. Or
          add one from this computer.
        </EmptyState>
      )}
      {items.length > 0 && (
        <ul className={s.grid} role="list">
          {items.map((item) => (
            <li key={item.id}>
              <CardLink to={`/library/${item.id}`} className={s.card}>
                <ItemThumb url={item.thumbnailUrl} kind={item.kind} />
                <span className={s.cardText}>
                  <span className={s.cardName}>{item.name}</span>
                  <span className={s.cardMeta}>
                    {[
                      kindWord(item.kind),
                      item.latest.version ? `Version ${item.latest.version}` : null,
                      count(item.versions, 'version'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <span className={s.cardMeta}>
                    {item.shares > 0 && (
                      <>
                        <ShareNetworkIcon size={14} weight="bold" aria-hidden="true" /> Shared with{' '}
                        {count(item.shares, 'person', 'people')} ·{' '}
                      </>
                    )}
                    Updated {dayText(item.updatedAt)}
                  </span>
                </span>
              </CardLink>
            </li>
          ))}
        </ul>
      )}
    </LibraryPage>
  );
}

/** Shared with the owner by others (which they can decline), and TUNE's for the groups they're in. */
export function SharedWithYou() {
  const api = useAuth().api('owners');
  const shared = useQuery({ queryKey: keys.shared, queryFn: () => data(api.GET('/shared')) });
  const items = shared.data?.items ?? [];
  return (
    <LibraryPage tab="Library: Shared with you">
      {shared.isPending && <Skeleton blocks={2} lines={0} label="Loading what's shared with you" />}
      {shared.isError && <ErrorBanner retry={() => void shared.refetch()}>What's shared couldn't load.</ErrorBanner>}
      {shared.isSuccess && items.length === 0 && (
        <EmptyState icon={<ShareNetworkIcon size={56} />} title="Nothing shared yet">
          When someone shares a dashboard or config with your email, or TUNE gives one to a group you're in, it's here,
          and on your MFDs.
        </EmptyState>
      )}
      {items.length > 0 && (
        <ul className={s.list} role="list">
          {items.map((item) => (
            <SharedRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </LibraryPage>
  );
}

function SharedRow({ item }: { item: SharedItem }) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const say = useToast();
  const path = { params: { path: { itemId: item.id } } };
  const download = useMutation({
    mutationFn: () => data(api.GET('/library/{itemId}/download', path)),
    onSuccess: (link) => fetchFile(link.url),
  });
  const decline = useMutation({
    mutationFn: () => data(api.DELETE('/shared/{itemId}', path)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.shared });
      say(`${item.name} is declined.`);
    },
  });
  const ask = async () => {
    const yes = await confirm({
      title: `Decline ${item.name}?`,
      message: "It goes from your MFDs at their next sync. Whoever shared it isn't told.",
      confirm: 'Decline',
    });
    if (yes) {
      decline.mutate();
    }
  };
  const failed = download.error ?? decline.error;
  return (
    <li className={s.row}>
      <ItemThumb url={item.thumbnailUrl} kind={item.kind} />
      <div className={s.rowText}>
        <h2 className={s.rowName}>{item.name}</h2>
        <p className={s.cardMeta}>
          {sourceWords(item.source)} · {kindWord(item.kind)}
          {item.version ? ` · Version ${item.version}` : ''} · {size(item.size)}
        </p>
        {item.description && <p className={s.rowDescription}>{item.description}</p>}
        {failed && <Banner tone="error">{problem(failed)}</Banner>}
        <div className={s.rowActions}>
          <Button variant="gray" size="small" busy={download.isPending} onClick={() => download.mutate()}>
            Download
          </Button>
          {item.source.kind === 'shared' && (
            <Button variant="destructivePlain" size="small" busy={decline.isPending} onClick={() => void ask()}>
              Decline
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

/** TUNE's catalogue: TUNE's dashboards and configs, each put on the owner's cars with a switch. */
export function Catalogue() {
  const api = useAuth().api('owners');
  const catalogue = useQuery({ queryKey: keys.catalogue, queryFn: () => data(api.GET('/catalogue')) });
  const vehicles = useVehicles();
  const entries = catalogue.data?.items ?? [];
  const cars = vehicles.data?.vehicles ?? [];
  return (
    <LibraryPage tab="Library: TUNE's catalogue">
      {catalogue.isPending && <Skeleton blocks={2} lines={0} label="Loading TUNE's catalogue" />}
      {catalogue.isError && (
        <ErrorBanner retry={() => void catalogue.refetch()}>TUNE's catalogue couldn't load.</ErrorBanner>
      )}
      {catalogue.isSuccess && entries.length === 0 && (
        <EmptyState icon={<SquaresFourIcon size={56} />} title="Nothing listed yet">
          TUNE's own dashboards and configs show here once TUNE lists them.
        </EmptyState>
      )}
      {entries.length > 0 && (
        <ul className={s.list} role="list">
          {entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} cars={cars} />
          ))}
        </ul>
      )}
    </LibraryPage>
  );
}

function EntryRow({ entry, cars }: { entry: Entry; cars: readonly Vehicle[] }) {
  const [choosing, setChoosing] = useRouteSheet(`cars-${entry.id}`);
  const on = cars.filter((c) => entry.vehicles.includes(c.id));
  return (
    <li className={s.row}>
      <ItemThumb url={entry.thumbnailUrl} kind={entry.kind} />
      <div className={s.rowText}>
        <h2 className={s.rowName}>{entry.name}</h2>
        <p className={s.cardMeta}>
          {[
            kindWord(entry.kind),
            entry.version ? `Version ${entry.version}` : null,
            entry.author ? `by ${entry.author}` : null,
            size(entry.size),
            entry.minAppVersion ? `needs TUNE Live ${entry.minAppVersion}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {entry.description && <p className={s.rowDescription}>{entry.description}</p>}
        <p className={on.length > 0 ? s.onCars : s.cardMeta}>
          {on.length > 0 ? `On ${on.map((c) => c.nickname).join(', ')}` : 'On none of your cars'}
        </p>
        <div className={s.rowActions}>
          {cars.length > 0 ? (
            <Button variant="gray" size="small" onClick={() => setChoosing(true)}>
              Choose cars<span className="visually-hidden"> for {entry.name}</span>
            </Button>
          ) : (
            <p className={s.cardMeta}>Add a car to your garage to put it on one.</p>
          )}
        </div>
      </div>
      <CarsSheet entry={entry} cars={cars} open={choosing} onClose={() => setChoosing(false)} />
    </li>
  );
}

/** Which of the owner's cars have one of TUNE's items: a switch each, saved as it's flipped. */
function CarsSheet({
  entry,
  cars,
  open,
  onClose,
}: {
  entry: Entry;
  cars: readonly Vehicle[];
  open: boolean;
  onClose: () => void;
}) {
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const set = async (vehicleId: string, on: boolean) => {
    const path = { params: { path: { vehicleId, itemId: entry.id } } };
    await data(
      on
        ? api.PUT('/vehicles/{vehicleId}/library/{itemId}', path)
        : api.DELETE('/vehicles/{vehicleId}/library/{itemId}', path),
    );
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.catalogue }),
      queryClient.invalidateQueries({ queryKey: keys.vehicleLibrary(vehicleId) }),
    ]);
  };
  return (
    <Sheet open={open} onClose={onClose} title={entry.name} cancelLabel="Done">
      <p className={s.note}>
        Switched on, it goes to that car's MFD at its next sync, with TUNE's newer versions as they come.
      </p>
      <ListSection header="Your cars" level={3}>
        {cars.map((car) => (
          <ListControl
            key={car.id}
            label={car.nickname}
            control={<SavingSwitch checked={entry.vehicles.includes(car.id)} save={(on) => set(car.id, on)} />}
          />
        ))}
      </ListSection>
    </Sheet>
  );
}
